// --- NEW FILE: packages/api-client/src/services/projectService.ts ---

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { 
    ServiceResponse,
    Project,
    Sprint,
    PublicProjectDetailsResponse,
    PublicProjectBoardResponse,
    UUID,
} from '@dopomogai/types';

// The base URL prefix for all public project operations.
const PUBLIC_PROJECTS_PREFIX = '/projects';

/**
 * A collection of service functions for interacting with the public-facing
 * Project API endpoints.
 */
export const projectService = {
  /**
   * Fetches a simple list of all public projects.
   */
  listPublicProjects: (): ServiceResponse<Project[]> => {
    return getApiClient().get<Project[]>(PUBLIC_PROJECTS_PREFIX)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing all public projects'));
  },

  /**
   * Fetches the details for a single project, hydrated with its language pack.
   * @param projectId The UUID of the project to fetch.
   * @param locale The desired language for the translations.
   */
  getPublicProjectDetails: (projectId: UUID, locale: string): ServiceResponse<PublicProjectDetailsResponse> => {
    return getApiClient().get<PublicProjectDetailsResponse>(`${PUBLIC_PROJECTS_PREFIX}/${projectId}`, { params: { locale } })
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching details for project '${projectId}'`));
  },

  /**
   * Fetches a list of all sprints belonging to a specific project.
   * Note: Returns raw sprint data without translations.
   * @param projectId The UUID of the project.
   */
  listSprintsInProject: (projectId: UUID): ServiceResponse<Sprint[]> => {
    return getApiClient().get<Sprint[]>(`${PUBLIC_PROJECTS_PREFIX}/${projectId}/sprints`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `listing sprints for project '${projectId}'`));
  },

  /**
   * Fetches the data for a project's global Kanban board. This includes all
   * unassigned tasks and the project-level language pack for translations.
   * @param projectId The UUID of the project.
   * @param locale The desired language for the translations.
   */
  getProjectBoard: (projectId: UUID, locale: string): ServiceResponse<PublicProjectBoardResponse> => {
    return getApiClient().get<PublicProjectBoardResponse>(`${PUBLIC_PROJECTS_PREFIX}/${projectId}/board`, { params: { locale } })
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching board for project '${projectId}'`));
  },
};